import { useEffect, useMemo, useState } from "react";
import { Megaphone, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface AnnouncementItem {
  id: number;
  title?: string | null;
  message: string;
  display_type?: "banner" | "popup";
  updated_at: string;
}

const STORAGE_KEY = "dismissed_announcements";
// Messages longer than this collapse behind a "Show more" toggle.
const COLLAPSE_THRESHOLD = 320;

/** Map of announcement id → the updated_at value that was dismissed. */
function readDismissed(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}") as Record<string, string>;
  } catch {
    return {};
  }
}

function persistDismissed(id: number, updatedAt: string) {
  const dismissed = readDismissed();
  dismissed[String(id)] = updatedAt;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissed));
}

export default function AnnouncementPopup() {
  const [queue, setQueue] = useState<AnnouncementItem[]>([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api
      .get<AnnouncementItem[]>("/announcements/active")
      .then((items) => {
        const dismissed = readDismissed();
        // Show popups that haven't been dismissed at their current version
        // (editing an announcement bumps updated_at, so it re-appears).
        const pending = items.filter(
          (a) =>
            (a.display_type ?? "banner") === "popup" &&
            dismissed[String(a.id)] !== a.updated_at,
        );
        setQueue(pending);
      })
      .catch(() => {});
  }, []);

  const current = queue[0];

  // Reset the expand state whenever we advance to a new announcement.
  useEffect(() => {
    setExpanded(false);
  }, [current?.id]);

  const isLong = useMemo(
    () => Boolean(current && current.message.length > COLLAPSE_THRESHOLD),
    [current],
  );

  if (!current) return null;

  const dismiss = () => {
    persistDismissed(current.id, current.updated_at);
    setQueue((prev) => prev.slice(1));
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-foreground/50 backdrop-blur-md" onClick={dismiss} />

      {/* Dialog */}
      <div className="public-auth-card relative w-full sm:max-w-[460px] sm:mx-4 rounded-t-[2rem] sm:rounded-[2rem] flex flex-col max-h-[88dvh] overflow-hidden">
        {/* Top primary rule */}
        <div className="h-[3px] w-full bg-gradient-to-r from-primary via-primary-light to-primary-glow shrink-0" />

        {/* Drag handle – mobile only */}
        <div className="flex justify-center pt-3 sm:hidden shrink-0">
          <div className="h-1 w-10 rounded-full bg-border/70" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 sm:px-7 pt-4 sm:pt-6 pb-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[1.1rem] bg-primary/10 text-primary ring-1 ring-primary/10">
              <Megaphone className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div className="min-w-0">
              <p className="eyebrow text-primary/80">Announcement</p>
              <h2 className="text-[1.15rem] sm:text-xl font-bold text-foreground leading-snug font-display truncate">
                {current.title?.trim() || "What's new"}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss announcement"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/70 bg-muted/60 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 pb-2">
          <div className="relative">
            <p
              className={cn(
                "whitespace-pre-line text-sm leading-relaxed text-foreground/90",
                isLong && !expanded && "line-clamp-6",
              )}
            >
              {current.message}
            </p>
            {isLong && !expanded && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-card to-transparent" />
            )}
          </div>

          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 text-sm font-semibold text-primary hover:underline underline-offset-2"
            >
              {expanded ? "Show less" : "Show more"}
            </button>
          )}

          <a
            href="https://www.businesslocating.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex text-xs font-medium text-muted-foreground hover:text-primary"
          >
            Review the full Terms &amp; Conditions →
          </a>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-border/50 bg-background/60 backdrop-blur-sm px-5 sm:px-7 py-4">
          <Button onClick={dismiss} className="w-full rounded-xl">
            Got it
          </Button>
          {queue.length > 1 && (
            <p className="mt-2 text-center text-[0.68rem] text-muted-foreground/60">
              {queue.length - 1} more announcement{queue.length - 1 === 1 ? "" : "s"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
